import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ADMIN_ACCOUNT_QUERY_KEY } from './useAdminAccountQuery';

/** Cap on the justification, mirroring the API's `AccountService.SpecialistChangeJustificationMaxLength`. */
export const SPECIALIST_JUSTIFICATION_MAX = 1000;

/** How old an account must be before a specialist reassignment becomes an audited change. */
const SPECIALIST_AUDIT_WINDOW_DAYS = 60;

export interface IUpdateCustomerSuccessSpecialistRequest {
	accountId: string;
	/** The new specialist's user id. */
	userId: string;
	/** Required only for an audited change; see {@link requiresSpecialistChangeJustification}. */
	justification?: string;
}

/**
 * True when reassigning `persisted`'s Customer Success specialist to `nextSpecialistId` is an audited
 * change, and so needs a justification: one real specialist replaced by a *different* real specialist on
 * an account created more than {@link SPECIALIST_AUDIT_WINDOW_DAYS} days ago.
 *
 * A first assignment (no specialist yet) is routine, and so is a change during the account's first 60
 * days, while assignments are still being settled. An account with no creation date cannot be shown to be
 * past the window, so it is treated as inside it.
 *
 * This mirrors `AccountService.RequiresSpecialistChangeAudit` on the API, which is the enforcing copy —
 * this one only decides whether to prompt, so the two disagreeing costs a rejected save, not a missed
 * audit.
 */
export const requiresSpecialistChangeJustification = (
	persisted: Api.IAccount | undefined,
	nextSpecialistId: string | undefined,
	now: Date = new Date()
): boolean => {
	const previousSpecialistId = persisted?.additionalInfo?.customerSuccessId?.trim();
	const next = nextSpecialistId?.trim();

	if (!previousSpecialistId || !next || previousSpecialistId === next) {
		return false;
	}

	const creationDate = persisted?.creationDate;
	if (!creationDate) {
		return false;
	}

	const ageInDays = (now.getTime() - new Date(creationDate).getTime()) / (1000 * 60 * 60 * 24);
	return ageInDays > SPECIALIST_AUDIT_WINDOW_DAYS;
};

/**
 * Assigns an account's Customer Success specialist via
 * `PATCH admin/account/{id}/customerSuccessSpecialist`.
 *
 * This is a separate endpoint from the account PUT for two reasons: the PUT has nowhere to carry the
 * justification, and it rejects an audited reassignment outright so it cannot be used to route around the
 * audit email. The overview therefore commits the specialist here, the same way it handles next meeting
 * and the managing account, and rebases its PUT payload onto the account this returns so the pair the PUT
 * carries matches what is now stored rather than tripping the same guard.
 *
 * Errors are surfaced through the rejected `mutateAsync` promise (there is no global `MutationCache`) so
 * the caller can distinguish a rejected justification from a failed save.
 */
export function useUpdateCustomerSuccessSpecialistMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ accountId, userId, justification }: IUpdateCustomerSuccessSpecialistRequest) =>
			userSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${encodeURIComponent(accountId)}/customerSuccessSpecialist`,
				'PATCH',
				{ userId, justification: justification?.trim() || undefined }
			),
		onSuccess: savedAccount => {
			if (savedAccount?.id) {
				queryClient.setQueryData([ADMIN_ACCOUNT_QUERY_KEY, savedAccount.id], savedAccount);
			}
		},
	});
}
